import { useState, useEffect, useRef } from 'react';
import { Modal } from '../common/Modal';
import { Credential } from '../../hooks/useCredentials';
import { X, Eye, EyeOff, Camera, Upload, Keyboard, QrCode, Info } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../api/client';
import { parseOtpAuthUrl, decodeQrCode, generateTotpCode, getTotpRemainingSeconds, ParsedTotp } from '../../utils/qr';
import { useCatalog } from '../../hooks/useCatalog';
import { CatalogCourt, buildCourtFields, findCourt, nextLabel } from '../../utils/catalog';
import { CourtPicker } from './CourtPicker';
import { Holder, formatarOab } from '../../utils/holders';
import { campoDoEmailDoCodigoVisivel, emailDoCodigoValido } from '../../utils/mfaInbox';

interface CredentialFormProps {
  initialData?: Credential | null;
  onSubmit: (payload: any) => Promise<void>;
  onClose: () => void;
  /** Titulares para o seletor "Titular" (Etapa 10a). Pasta aberta na tela = sugestão para credencial nova. */
  holders?: Holder[];
  defaultHolderId?: string | null;
  /** D23: atalho "+ Criar titular" ao lado do campo quando não há titulares (abre o "Gerenciar titulares"); só owner/admin. */
  onManageHolders?: () => void;
}

function guessSystemType(label: string, urls: string): string {
  const text = (label + ' ' + urls).toLowerCase();
  if (text.includes('eproc')) return 'eproc';
  if (text.includes('pje')) return 'pje';
  if (text.includes('esaj') || text.includes('e-saj')) return 'esaj';
  if (text.includes('projudi')) return 'projudi';
  return 'other';
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function CredentialForm({ initialData, onSubmit, onClose, holders = [], defaultHolderId = null, onManageHolders }: CredentialFormProps) {
  const { membership } = useAuth();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';

  const [formData, setFormData] = useState({
    label: '',
    login: '',
    password: '',
    totp_secret: '',
  });
  const [urls, setUrls] = useState<string[]>(['']);
  const { catalog, error: catalogError } = useCatalog();
  const [courtKey, setCourtKey] = useState<string | null>(initialData?.catalog_key ?? null);
  const [holderId, setHolderId] = useState<string>((initialData ? initialData.holder_id : defaultHolderId) || '');
  // Etapa 13: e-mail que recebe o código do tribunal (a API devolve `mfa_email_address`; o tipo da lista ainda não o declara).
  const emailDoCodigoSalvo = (initialData as (Credential & { mfa_email_address?: string | null }) | null | undefined)?.mfa_email_address ?? '';
  const [mfaEmail, setMfaEmail] = useState<string>(emailDoCodigoSalvo);
  const lastSuggestedLabel = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showTotpSecret, setShowTotpSecret] = useState(false);

  // States and refs for local QR code reading & TOTP validation
  const [totpMode, setTotpMode] = useState<'manual' | 'camera' | 'upload'>('manual');
  const [parsedTotp, setParsedTotp] = useState<ParsedTotp | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [liveCode, setLiveCode] = useState<string>('');
  const [liveTimeLeft, setLiveTimeLeft] = useState<number>(30);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scanIntervalRef = useRef<number | null>(null);

  // Live TOTP generator for validation
  useEffect(() => {
    let activeSecret = '';
    let opts = {};

    if (parsedTotp) {
      activeSecret = parsedTotp.secret;
      opts = { algorithm: parsedTotp.algorithm, digits: parsedTotp.digits, period: parsedTotp.period };
    } else if (totpMode === 'manual' && formData.totp_secret && formData.totp_secret.length >= 8) {
      const cleanSecret = formData.totp_secret.replace(/[\s=-]/g, '').toUpperCase();
      // Basic check for base32 alphabet
      if (/^[A-Z2-7]+$/.test(cleanSecret)) {
        activeSecret = cleanSecret;
      }
    }

    if (!activeSecret) {
      setLiveCode('');
      return;
    }

    const updateCode = () => {
      const code = generateTotpCode(activeSecret, opts);
      setLiveCode(code);
      setLiveTimeLeft(getTotpRemainingSeconds((opts as any).period || 30));
    };

    updateCode();
    const interval = setInterval(updateCode, 1000);
    return () => clearInterval(interval);
  }, [parsedTotp, formData.totp_secret, totpMode]);

  // Camera capture effect
  useEffect(() => {
    if (totpMode !== 'camera' || parsedTotp) {
      if (scanIntervalRef.current) {
        cancelAnimationFrame(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      return;
    }

    let activeStream: MediaStream | null = null;
    setQrError(null);

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } }
        });
        activeStream = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        const scanFrame = () => {
          if (!videoRef.current || !canvasRef.current) {
            scanIntervalRef.current = requestAnimationFrame(scanFrame);
            return;
          }

          const video = videoRef.current;
          const canvas = canvasRef.current;
          
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const qrText = decodeQrCode(imageData);
              if (qrText) {
                const parsed = parseOtpAuthUrl(qrText);
                if (parsed) {
                  setParsedTotp(parsed);
                  stream.getTracks().forEach(track => track.stop());
                  activeStream = null;
                  return;
                } else {
                  setQrError('Código QR detectado, mas não contém um link ou segredo TOTP válido.');
                }
              }
            }
          }
          scanIntervalRef.current = requestAnimationFrame(scanFrame);
        };

        scanIntervalRef.current = requestAnimationFrame(scanFrame);
      } catch (err: any) {
        console.error('[Camera] access error:', err);
        setQrError('Não foi possível acessar a câmera. Verifique se deu as permissões de acesso.');
        setTotpMode('manual');
      }
    };

    startCamera();

    return () => {
      if (scanIntervalRef.current) {
        cancelAnimationFrame(scanIntervalRef.current);
      }
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [totpMode, parsedTotp]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file);
  };

  const processImageFile = (file: File) => {
    setQrError(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Upscale small images for better QR detection
        const MIN_DIM = 800;
        let drawWidth = img.width;
        let drawHeight = img.height;
        if (drawWidth < MIN_DIM || drawHeight < MIN_DIM) {
          const scale = Math.max(MIN_DIM / drawWidth, MIN_DIM / drawHeight);
          drawWidth = Math.round(drawWidth * scale);
          drawHeight = Math.round(drawHeight * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = drawWidth;
        canvas.height = drawHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Use better interpolation for upscaling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, drawWidth, drawHeight);
          const imageData = ctx.getImageData(0, 0, drawWidth, drawHeight);
          const qrText = decodeQrCode(imageData);
          if (qrText) {
            const parsed = parseOtpAuthUrl(qrText);
            if (parsed) {
              setParsedTotp(parsed);
            } else {
              setQrError('QR Code detectado, mas não contém um link ou segredo TOTP válido.');
            }
          } else {
            setQrError('Nenhum QR Code encontrado na imagem. Tente uma captura de tela mais nítida ou com fundo claro.');
          }
        }
      };
      img.onerror = () => {
        setQrError('Erro ao abrir o arquivo de imagem.');
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmParsedTotp = () => {
    if (!parsedTotp) return;
    setFormData(prev => ({
      ...prev,
      totp_secret: parsedTotp.secret,
      label: prev.label ? prev.label : parsedTotp.issuer,
      login: prev.login ? prev.login : parsedTotp.account,
    }));
    setParsedTotp(null);
    setTotpMode('manual');
  };

  useEffect(() => {
    const loadRevealedData = async () => {
      if (initialData) {
        let revealedLogin = '';
        let revealedPassword = '';
        let revealedTotp = '';
        
        if (isAdmin) {
          try {
            const data = await apiClient.get<any>(`/credentials/${initialData.id}?reveal=true`);
            revealedLogin = data.login || '';
            revealedPassword = data.password || '';
            revealedTotp = data.totp_secret || '';
          } catch (err) {
            console.error('Failed to prefill revealed data', err);
          }
        }
        
        setFormData({
          label: initialData.label,
          login: revealedLogin,
          password: revealedPassword,
          totp_secret: revealedTotp,
        });
        setUrls(initialData.url_patterns?.length ? initialData.url_patterns : ['']);
      }
    };

    loadRevealedData();
  }, [initialData, isAdmin]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCourtChange = (court: CatalogCourt | null) => {
    setCourtKey(court?.key ?? null);
    setFormData(prev => ({ ...prev, label: nextLabel(prev.label, lastSuggestedLabel.current, court) }));
    lastSuggestedLabel.current = court ? court.label : null;
  };

  // Etapa 13: mesmo critério do servidor para o sistema (o do tribunal escolhido, ou o da credencial salva).
  const sistemaDaCredencial = findCourt(catalog, courtKey)?.system ?? initialData?.system_type ?? guessSystemType(formData.label, urls.join(' '));
  const mostrarEmailDoCodigo = campoDoEmailDoCodigoVisivel(membership, sistemaDaCredencial);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mostrarEmailDoCodigo && mfaEmail.trim() && !emailDoCodigoValido(mfaEmail)) {
      setError('Informe um e-mail válido em "E-mail que recebe o código do tribunal".');
      return;
    }
    setIsSubmitting(true);

    const system = initialData?.system_type || guessSystemType(formData.label, urls.join(' '));
    const slug = initialData?.tribunal_slug || slugify(formData.label);

    let finalUrls = urls.map(s => s.trim()).filter(Boolean);
    
    if (system === 'eproc') {
      const match = slug.match(/(tj[a-z]{2}|trf[1-6])/);
      if (match) {
        const court = match[0];
        const ssoUrl = `https://sso.${court}.jus.br/realms/eproc/`;
        if (!finalUrls.includes(ssoUrl)) {
          finalUrls.push(ssoUrl);
        }
      }
    }

    const payload: any = {
      label: formData.label,
      ...buildCourtFields(courtKey, { system_type: system, tribunal_slug: slug || 'credencial', url_patterns: finalUrls }),
    };
    // Titular: só envia quando muda (credencial nova sem titular não precisa do campo).
    if ((holderId || null) !== (initialData?.holder_id || null)) payload.holder_id = holderId || null;

    // E-mail do código: só quando o campo está na tela e mudou (vazio apaga o que estava salvo).
    if (mostrarEmailDoCodigo && mfaEmail.trim() !== emailDoCodigoSalvo) payload.mfa_email_address = mfaEmail.trim() || null;

    if (formData.login) payload.login_plaintext = formData.login;
    if (formData.password) payload.password_plaintext = formData.password;
    if (formData.totp_secret) payload.totp_secret_plaintext = formData.totp_secret;

    try {
      await onSubmit(payload);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar credencial');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal pedirParaFechar={onClose} protegerDigitacao>
      <div style={{
        background: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-default)',
        borderRadius: 'var(--radius-xl)',
        width: '100%',
        maxWidth: '500px',
        padding: 'var(--space-8)',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
            {initialData ? 'Editar credencial' : 'Nova credencial'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar" title="Fechar" className="botao-icone botao-icone--sem-borda">
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Rótulo / Nome</label>
            <input name="label" value={formData.label} onChange={handleChange} required placeholder="ex: eProc TRF4 - Dr. Silva" style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} />
          </div>

          <CourtPicker
            catalog={catalog}
            catalogError={!!catalogError}
            value={courtKey}
            urlsForSuggestion={urls}
            onChange={handleCourtChange}
            depoisDoTribunal={
              // D23: o "Titular" aparece sempre e perto do topo (sem titulares: só "Sem titular" e o atalho para criar).
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                  <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Titular (opcional)</label>
                  {onManageHolders && holders.length === 0 && (
                    <button
                      type="button"
                      onClick={onManageHolders}
                      style={{ background: 'transparent', border: 'none', color: 'var(--color-primary-400)', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', padding: 0 }}
                    >
                      + Criar titular
                    </button>
                  )}
                </div>
                <select value={holderId} onChange={(e) => setHolderId(e.target.value)} style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', width: '100%', minWidth: 0 }}>
                  <option value="">Sem titular</option>
                  {holders.map((holder) => (
                    <option key={holder.id} value={holder.id}>
                      {[holder.name, formatarOab(holder)].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>
              </div>
            }
          >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>URLs para Autofill</label>
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: 'var(--space-2)', 
              padding: 'var(--space-2) var(--space-3)', 
              background: 'rgba(26, 154, 102, 0.06)', 
              border: '1px solid rgba(26, 154, 102, 0.15)', 
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-1)'
            }}>
              <Info size={14} style={{ color: 'var(--color-primary-400)', marginTop: '2px', flexShrink: 0 }} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                <strong style={{ color: 'var(--color-primary-300)' }}>Dica:</strong> Adicione as URLs onde esta credencial deve ser preenchida automaticamente.
              </span>
            </div>
            {urls.map((url, index) => (
              <div key={index} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <input 
                  value={url} 
                  onChange={(e) => {
                    const newUrls = [...urls];
                    newUrls[index] = e.target.value;
                    setUrls(newUrls);
                  }} 
                  placeholder="ex: eproc.trf4.jus.br" 
                  style={{ flex: 1, background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} 
                />
                <button 
                  type="button"
                  onClick={() => setUrls(urls.filter((_, i) => i !== index))}
                  style={{ background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-error)', padding: 'var(--space-2)', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}
                  disabled={urls.length === 1}
                >
                  <X size={18} />
                </button>
              </div>
            ))}
            <button 
              type="button" 
              onClick={() => setUrls([...urls, ''])} 
              style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: 'var(--color-primary-400)', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: 'var(--space-1)' }}
            >
              + Adicionar URL
            </button>
          </div>
          </CourtPicker>

          <hr style={{ border: 'none', borderTop: '1px solid var(--color-border-default)', margin: 'var(--space-4) 0' }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Login / CPF / OAB {initialData && '(deixe em branco para manter)'}</label>
            <input name="login" value={formData.login} onChange={handleChange} required={!initialData} style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Senha {initialData && '(deixe em branco para manter)'}</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input 
                type={showPassword ? "text" : "password"} 
                name="password" 
                value={formData.password} 
                onChange={handleChange} 
                required={!initialData} 
                style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', paddingRight: '40px', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', width: '100%', boxSizing: 'border-box' }} 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                style={{ position: 'absolute', right: '12px', background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Segredo TOTP (Opcional) {initialData && '(deixe em branco para manter)'}</label>
            
            {/* keyframe animation styles */}
            <style>{`
              @keyframes scanLine {
                0% { top: 0%; }
                50% { top: 100%; }
                100% { top: 0%; }
              }
            `}</style>

            {/* Segment control tabs */}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', background: 'var(--color-bg-root)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-default)' }}>
              <button
                type="button"
                onClick={() => { setTotpMode('manual'); setQrError(null); setParsedTotp(null); }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) 0',
                  borderRadius: 'var(--radius-md)',
                  background: totpMode === 'manual' ? 'var(--color-bg-surface)' : 'transparent',
                  border: 'none',
                  color: totpMode === 'manual' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontWeight: totpMode === 'manual' ? 'var(--font-semibold)' : 'var(--font-normal)',
                  boxShadow: totpMode === 'manual' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease',
                  fontSize: 'var(--text-xs)'
                }}
              >
                <Keyboard size={14} />
                Manual
              </button>
              <button
                type="button"
                onClick={() => { setTotpMode('upload'); setQrError(null); setParsedTotp(null); }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) 0',
                  borderRadius: 'var(--radius-md)',
                  background: totpMode === 'upload' ? 'var(--color-bg-surface)' : 'transparent',
                  border: 'none',
                  color: totpMode === 'upload' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontWeight: totpMode === 'upload' ? 'var(--font-semibold)' : 'var(--font-normal)',
                  boxShadow: totpMode === 'upload' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease',
                  fontSize: 'var(--text-xs)'
                }}
              >
                <Upload size={14} />
                Upload QR
              </button>
              <button
                type="button"
                onClick={() => { setTotpMode('camera'); setQrError(null); setParsedTotp(null); }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) 0',
                  borderRadius: 'var(--radius-md)',
                  background: totpMode === 'camera' ? 'var(--color-bg-surface)' : 'transparent',
                  border: 'none',
                  color: totpMode === 'camera' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  fontWeight: totpMode === 'camera' ? 'var(--font-semibold)' : 'var(--font-normal)',
                  boxShadow: totpMode === 'camera' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s ease',
                  fontSize: 'var(--text-xs)'
                }}
              >
                <Camera size={14} />
                Câmera
              </button>
            </div>

            {/* Error notifications */}
            {qrError && (
              <div style={{
                padding: 'var(--space-2) var(--space-3)',
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                marginBottom: 'var(--space-2)'
              }}>
                {qrError}
              </div>
            )}

            {/* Sub-interfaces depending on current state */}
            {parsedTotp ? (
              // Confirmation View (Valid QR Decoded)
              <div style={{
                background: 'var(--color-bg-root)',
                border: '1px solid var(--color-primary-500, #10b981)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: '#10b981', fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-xs)' }}>
                  <QrCode size={16} />
                  <span>Segredo TOTP encontrado!</span>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', fontSize: 'var(--text-xs)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr' }}>
                    <span style={{ color: 'var(--color-text-secondary)' }}>Emissor:</span>
                    <strong style={{ color: 'var(--color-text-primary)' }}>{parsedTotp.issuer}</strong>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr' }}>
                    <span style={{ color: 'var(--color-text-secondary)' }}>Conta:</span>
                    <span style={{ color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>{parsedTotp.account}</span>
                  </div>
                </div>

                {liveCode && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'var(--color-bg-surface)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border-default)'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Código Atual</span>
                      <strong style={{ fontSize: 'var(--text-lg)', color: 'var(--color-primary-400, #10b981)', letterSpacing: '2px', fontFamily: 'monospace' }}>
                        {liveCode.slice(0, 3)} {liveCode.slice(3)}
                      </strong>
                    </div>
                    
                    {/* Visual Countdown */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ position: 'relative', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="20" height="20" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
                          <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255, 255, 255, 0.1)" strokeWidth="4" />
                          <circle cx="18" cy="18" r="16" fill="none" stroke="#10b981" strokeWidth="4"
                            strokeDasharray={`${(liveTimeLeft / parsedTotp.period) * 100}, 100`}
                            style={{ transition: 'stroke-dasharray 1s linear' }}
                          />
                        </svg>
                        <span style={{ position: 'absolute', fontSize: '9px', fontWeight: 'bold', color: 'var(--color-text-primary)' }}>
                          {liveTimeLeft}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    onClick={() => { setParsedTotp(null); setQrError(null); }}
                    style={{
                      flex: 1,
                      padding: 'var(--space-2)',
                      background: 'transparent',
                      border: '1px solid var(--color-border-default)',
                      color: 'var(--color-text-secondary)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      fontSize: 'var(--text-xs)'
                    }}
                  >
                    Escanear Outro
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmParsedTotp}
                    style={{
                      flex: 1.5,
                      padding: 'var(--space-2)',
                      background: 'var(--color-primary-600)',
                      color: 'var(--color-primary-50)',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px'
                    }}
                  >
                    Confirmar e Preencher
                  </button>
                </div>
              </div>
            ) : totpMode === 'camera' ? (
              // Webcam Scanner View
              <div style={{
                position: 'relative',
                width: '100%',
                height: '200px',
                background: '#000',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                
                {/* Target scanning overlay */}
                <div style={{
                  position: 'absolute',
                  width: '130px',
                  height: '130px',
                  border: '2px solid var(--color-primary-500, #10b981)',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box'
                }}>
                  {/* Glowing Laser Scanline */}
                  <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: '2px',
                    background: 'var(--color-primary-500, #10b981)',
                    boxShadow: '0 0 8px var(--color-primary-400, #34d399)',
                    animation: 'scanLine 2s infinite linear',
                    left: 0
                  }} />
                </div>
                
                {/* Scanning feedback label */}
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.7)',
                  backdropFilter: 'blur(4px)',
                  padding: '4px 12px',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-primary-300)',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                }}>
                  Buscando QR Code...
                </div>
                {/* Hidden canvas used internally */}
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
            ) : totpMode === 'upload' ? (
              // Drag & Drop Image Upload View
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) processImageFile(file);
                }}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--color-border-default)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-6) var(--space-4)',
                  textAlign: 'center',
                  background: 'var(--color-bg-root)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  transition: 'border-color 0.2s ease'
                }}
              >
                <Upload size={28} color="var(--color-text-secondary)" style={{ marginBottom: '6px' }} />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>
                  Arraste um print/imagem do QR Code
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                  ou clique para selecionar arquivo
                </span>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
            ) : (
              // Standard Manual Input with live code validation below it
              <>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input 
                    type={showTotpSecret ? "text" : "password"} 
                    name="totp_secret" 
                    value={formData.totp_secret} 
                    onChange={(e) => setFormData(prev => ({ ...prev, totp_secret: e.target.value.replace(/ /g, '').toUpperCase() }))} 
                    placeholder="Chave base32 (ex: JBSWY3DPEHPK3PXP)" 
                    style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', paddingRight: '40px', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)', width: '100%', boxSizing: 'border-box' }} 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowTotpSecret(!showTotpSecret)} 
                    style={{ position: 'absolute', right: '12px', background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                  >
                    {showTotpSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                
                {liveCode && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'var(--color-bg-root)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border-default)',
                    marginTop: 'var(--space-2)'
                  }}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Código atual:</span>
                      <strong style={{ fontSize: 'var(--text-sm)', color: 'var(--color-primary-400, #10b981)', fontFamily: 'monospace', letterSpacing: '1px' }}>
                        {liveCode.slice(0, 3)} {liveCode.slice(3)}
                      </strong>
                    </div>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                      Expira em {liveTimeLeft}s
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {mostrarEmailDoCodigo && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>E-mail que recebe o código do tribunal (opcional)</label>
              <input type="email" name="mfa_email_address" value={mfaEmail} onChange={(e) => setMfaEmail(e.target.value)} maxLength={254} placeholder="ex: advogado@escritorio.com.br" style={{ background: 'var(--color-bg-root)', border: '1px solid var(--color-border-default)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', color: 'var(--color-text-primary)' }} />
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>
                O e-mail cadastrado no tribunal, que recebe o código de verificação do login. Ajuda o Lictor a saber de quem é cada código quando mais de um advogado entra ao mesmo tempo.
              </span>
            </div>
          )}

          {/* A2: rodapé fixo no fim da caixa: Cancelar e Salvar sempre à vista, mesmo com o formulário rolando. */}
          <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'var(--space-6)', position: 'sticky', bottom: 'calc(-1 * var(--space-8))', background: 'var(--color-bg-surface)', padding: 'var(--space-4) 0 var(--space-8)', marginBottom: 'calc(-1 * var(--space-8))', borderTop: '1px solid var(--color-border-default)', zIndex: 1 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 'var(--font-medium)' }}>
              Cancelar
            </button>
            <button type="submit" disabled={isSubmitting} style={{ flex: 1, padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontWeight: 'var(--font-medium)' }}>
              {isSubmitting ? 'Salvando...' : 'Salvar credencial'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
